import { C } from "../common/component";
import { Helper } from "../common/helper";
import { Requester } from "../common/requester";
import { Dom } from "../common/dom";
import { HierarchyService } from "./hierarchy-service";
import { NodeStateService } from "./node-state-service";
import { TreeRenderer } from "./tree-renderer";
import { SvgRenderer } from "./svg-renderer";

export interface OrgChartNode {
    Value: string;
    FormattedName: string;
}

export interface OrgChartRow {
    Node: OrgChartNode;
    ManagerId: string;
    Tooltip: string;
    Image: string;
    JobTitle: string;
}

export interface NodePosition {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface TreeNode {
    id: string;
    display: string;
    jobTitle: string;
    image?: string;
    tooltip?: string;
    children: TreeNode[];
}

export class OrganizationChart
{
    private readonly url: string;
    private originalData: OrgChartRow[] = [];

    private readonly hierarchyService: HierarchyService;
    private readonly nodeStateService: NodeStateService;
    private treeRenderer: TreeRenderer;
    private chartContainer: JQuery | null = null;

    constructor(url: string)
    {
        this.url = url;
        this.hierarchyService = new HierarchyService();
        this.nodeStateService = new NodeStateService();
        this.treeRenderer = new TreeRenderer(this.nodeStateService);
    }

    async appendTo(parent: JQuery): Promise<void>
    {
        const response: OrgChartRow[] = await Requester.get(this.url);

        const mainContainer = this.createMainContainer(parent);
        this.chartContainer = this.createChartContainer(mainContainer);

        this.originalData = response;

        this.hierarchyService.buildHierarchy(this.originalData);

        this.drawOrgChart();
    }

    private createMainContainer(parent: JQuery): JQuery
    {
        return C.div
            .css("width", "100%")
            .css("height", Helper.mainHeight)
            .css("display", "flex")
            .css("flex-direction", "column")
            .appendTo(parent);
    }

    private createChartContainer(mainContainer: JQuery): JQuery
    {
        return C.div
            .css("flex", "1")
            .css("border", "1px solid #ddd")
            .css("border-radius", "5px")
            .css("padding", "10px")
            .css("position", "relative")
            .appendTo(mainContainer);
    }

    private drawOrgChart(): void
    {
        this.drawLeftToRightChart();
    }

    private createSVGElement(): SVGElement
    {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.style.position = "absolute";
        svg.style.top = "0";
        svg.style.left = "0";
        svg.style.width = "100%";
        svg.style.height = "100%";
        svg.style.pointerEvents = "none";
        svg.style.zIndex = "1";
        return svg;
    }

    private drawLeftToRightChart(): void
    {
        if (!this.chartContainer)
        {
            return;
        }

        const container = this.chartContainer[0];
        container.innerHTML = "";

        const svg = this.createSVGElement();
        const contentDiv = Dom.createDiv();
        contentDiv.style.position = "relative";
        contentDiv.style.zIndex = "2";

        container.appendChild(svg);
        container.appendChild(contentDiv);

        const treeData = this.treeRenderer.buildTreeStructure(this.originalData);

        contentDiv.innerHTML = this.treeRenderer.createHorizontalTree(treeData);

        this.addEventHandlers();
        this.drawConnectingLines(svg, this.originalData);
    }

    private drawConnectingLines(svg: SVGElement, filteredData: OrgChartRow[]): void
    {
        const nodePositions = SvgRenderer.collectNodePositions(svg);
        SvgRenderer.drawConnectingLines(svg, nodePositions, filteredData);
    }

    private addEventHandlers(): void
    {
        const nodes = document.querySelectorAll(".horizontal-org-node");
        nodes.forEach(node =>
        {
            node.addEventListener("click", (e) => this.handleNodeClick(e));
            node.addEventListener("mouseenter", (e) => this.handleNodeHover(e));
            node.addEventListener("mouseleave", (e) => this.handleNodeLeave(e));
        });
    }

    private handleNodeClick(e: Event): void
    {
        e.stopPropagation();
        const nodeId = (e.currentTarget as HTMLElement).getAttribute("data-node-id");
        if (nodeId)
        {
            this.toggleCollapse(nodeId);
        }
    }

    private handleNodeHover(e: Event): void
    {
        const node = e.target as HTMLElement;
        node.style.backgroundColor = "#f0f8ff";
        node.style.borderColor = "#007bff";
        node.style.transform = "translateY(-1px)";
        node.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
    }

    private handleNodeLeave(e: Event): void
    {
        const node = e.target as HTMLElement;
        node.style.backgroundColor = "#ffffff";
        node.style.borderColor = "#666666";
        node.style.transform = "translateY(0)";
        node.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
    }

    private toggleCollapse(nodeId: string): void
    {
        this.nodeStateService.toggleCollapse(nodeId);
        this.drawOrgChart();
    }
}
